import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRightIcon, ExternalLinkIcon, InfoIcon } from 'lucide-react';
import { Seo } from '../components/ui/Seo';
import { PageHero } from '../components/ui/PageHero';
import { ButtonLink } from '../components/ui/ButtonLink';
import { IMAGES } from '../data/images';
import { BOOKING_URL, SITE } from '../data/site';
import { getCourse, priceLabel } from '../utils/courses';
const studentSlugs = ['nra-pistol-phase-2', 'nra-ppith', 'nra-ppoth', 'nra-rso'];
const instructorSlugs = ['nra-instructor-pistol', 'nra-instructor-ppith', 'nra-instructor-ppoth', 'nra-crso'];
function CourseList({
  id,
  title,
  intro,
  slugs
}: {
  id: string;
  title: string;
  intro: string;
  slugs: string[];
}) {
  return <section id={id} aria-labelledby={`${id}-title`} className="scroll-mt-28" data-id="element-850" data-mp="2p3tjg0a8ox3j">
      <h2 id={`${id}-title`} className="t-h2 text-navy" data-id="element-851" data-mp="zovga98s53yr">{title}</h2>
      <span className="red-rule mt-5" aria-hidden="true" data-id="element-852" data-mp="upiu7qpqg0x8" />
      <p className="mt-5 max-w-[40rem] text-steel" data-id="element-853" data-mp="ur3qplvlxtxq">{intro}</p>
      <ul className="mt-8 border-t border-navy/15" data-id="element-854" data-mp="2d00amcs4gfpf">
        {slugs.map(slug => {
        const c = getCourse(slug);
        if (!c) return null;
        return <li key={slug} className="border-b border-navy/15" data-id="element-855" data-mp="2lz3w1nerh773">
              <Link to={`/courses/${slug}`} className="group grid grid-cols-[1fr_auto] items-center gap-4 py-5" data-id="element-856" data-mp="2lxj1vywczq9r">
                <span data-id="element-857" data-mp="2vpfcuajjnave">
                  <span className="block font-display text-xl font-semibold uppercase text-navy group-hover:underline group-hover:decoration-accent group-hover:underline-offset-4" data-id="element-858" data-mp="2vpyjuw7noeun">{c.name.replace('NRA ', '')}</span>
                  <span className="text-sm text-steel" data-id="element-859" data-mp="35oo24q3gs1bf">{c.duration} · {c.benefit}</span>
                </span>
                <span className="flex items-center gap-3" data-id="element-860" data-mp="39nwx1ya8q58k">
                  <span className="font-display text-xl font-semibold text-navy" data-id="element-861" data-mp="1b9v98ytyton6">{priceLabel(c)}</span>
                  <ArrowRightIcon className="h-5 w-5 text-accent transition-transform duration-150 group-hover:translate-x-1" aria-hidden="true" data-id="element-862" data-mp="1faxd5sytuovh" />
                </span>
              </Link>
            </li>;
      })}
      </ul>
    </section>;
}
export function NraUscca() {
  return <>
      <Seo title="NRA & USCCA Courses Las Vegas" description="NRA student and instructor courses taught by an NRA Training Counselor, plus USCCA Defensive Shooting Fundamentals in Las Vegas." data-id="element-863" data-mp="2rnanj3q096g5" />
      <PageHero title="NRA & USCCA Courses" sub="Student courses and instructor certifications, taught by an NRA Training Counselor since 2014." image={IMAGES.classroom} crumbs={[{
      label: 'NRA & USCCA'
    }]} data-id="element-864" data-mp="31kf9p3jup1wy">
        <ButtonLink href={BOOKING_URL} size="lg" data-id="element-865" data-mp="3ba8hruksaywg">
          Book a Course
        </ButtonLink>
      </PageHero>

      <div className="gutter section grid gap-16 bg-white lg:grid-cols-2 lg:gap-20" data-id="element-866" data-mp="36drx5acsn2o4">
        <CourseList id="nra-student" title="NRA Student Courses" intro="Build knowledge, skill and attitude to the NRA standard." slugs={studentSlugs} data-id="element-867" data-mp="3kbzsjdu6lvx6" />
        <CourseList id="nra-instructor" title="NRA Instructor Courses" intro="Ready to teach? Train with an NRA Training Counselor." slugs={instructorSlugs} data-id="element-868" data-mp="2s9bnwu94jnl" />
      </div>

      <section className="gutter section brushed" aria-labelledby="uscca-title" data-id="element-869" data-mp="7phtfwbhmlob">
        <div className="grid gap-10 lg:grid-cols-12 lg:items-center" data-id="element-870" data-mp="3px779k02zj8j">
          <div className="lg:col-span-7" data-id="element-871" data-mp="3txr6qu84myxu">
            <p className="font-display text-sm uppercase tracking-[0.16em] text-chrome-300" data-id="element-872" data-mp="3u0wy6g48acwq">USCCA Certified Partner</p>
            <h2 id="uscca-title" className="t-h2 mt-4 text-white" data-id="element-873" data-mp="3ky45c8fedt4s">USCCA Defensive Shooting Fundamentals — Level 1</h2>
            <span className="red-rule mt-5" aria-hidden="true" data-id="element-874" data-mp="36a9aomhtxep2" />
            <p className="t-lead mt-6 max-w-[44rem] text-chrome-100" data-id="element-875" data-mp="367n68vq8jfb2">The USCCA foundation for responsible armed self-defense: safe handling, marksmanship fundamentals and a defensive mindset.</p>
          </div>
          <div className="flex flex-wrap gap-3 lg:col-span-5 lg:justify-end" data-id="element-876" data-mp="327mq96nhmw1w">
            <ButtonLink to="/courses/uscca-dsf" size="lg" data-id="element-877" data-mp="329pdxk164d3g">
              Course Details
            </ButtonLink>
            <ButtonLink href={SITE.usccaPartner} size="lg" variant="outline-light" newTab data-id="element-878" data-mp="2p83r1088i53q">
              USCCA Partner Page <ExternalLinkIcon className="h-4 w-4" aria-hidden="true" data-id="element-879" data-mp="2kh5b41r5r910" />
            </ButtonLink>
          </div>
        </div>
      </section>

      <section className="gutter py-14 bg-mist" aria-labelledby="prereq-callout" data-id="element-880" data-mp="2gf0fwyl2a4c9">
        <div className="flex gap-4 rounded-[4px] border-l-4 border-navy bg-white p-6 md:p-8" data-id="element-881" data-mp="2bhqk9172wwfz">
          <InfoIcon className="mt-1 h-6 w-6 shrink-0 text-navy" aria-hidden="true" data-id="element-882" data-mp="26hcke6fmsst1" />
          <div data-id="element-883" data-mp="21hzqx2u98nhh">
            <h2 id="prereq-callout" className="font-display text-xl font-semibold uppercase text-navy" data-id="element-884" data-mp="21jkl2rcnq4et">Prerequisites</h2>
            <p className="mt-2 max-w-[70rem] text-ink" data-id="element-885" data-mp="1xiika633dkaq">
              NRA Basic Pistol Phase II requires completing the online Phase I first. Instructor courses require a pre-course qualification and the corresponding student course. Chief RSO requires a current RSO certification. Call {SITE.phoneDisplay} if you’re unsure which course fits.
            </p>
          </div>
        </div>
      </section>
    </>;
}